import type { ItemDataType, ItemAlignment, DbGridColumn } from '@/types/grid'
import { formatDateTime, formatDate, formatTime, formatCNPJ, formatCpf, formatCel, formatFone } from '@/utils/masks'

export const useGrid = () => {
  const gridFontSize = 12
  // const DbGridColumns = ref<DbGridColumn[]>([])
  // const AgGridColumns = ref([])

  // const fieldCurrency = {
  //   filter: 'agNumberColumnFilter',
  //   type: 'numericColumn',
  //   aggFunc: 'sum',
  //   valueFormatter: p => parseFloat(p.value).toLocaleString('pt-BR', {
  //     style: 'currency',
  //     currency: 'BRL'
  //   })
  // }

  const fieldReal = {
    filter: 'agNumberColumnFilter',
    type: 'numericColumn',
    // aggFunc: 'sum',
    valueFormatter: p => parseFloat(p.value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })
  }

  const fieldNumber = {
    filter: 'agNumberColumnFilter',
    type: 'numericColumn',
    cellRenderer: 'agAnimateShowChangeCellRenderer'
    // aggFunc: 'sum'
  }

  const dateComparator = (filterDate: Date, cellValue: string) => {
    if (!cellValue) return -1
    const [y, m, d] = cellValue.split(/[-T ]/g).map(Number)
    const cellDay = new Date(y, m - 1, d)
    if (cellDay < filterDate) return -1
    if (cellDay > filterDate) return 1
    return 0
  }

  const fieldDateTime = {
    filter: 'agDateColumnFilter',
    filterParams: { comparator: dateComparator },
    valueFormatter: p => formatDateTime(p.value)
  }

  const fieldDate = {
    filter: 'agDateColumnFilter',
    filterParams: { comparator: dateComparator },
    valueFormatter: p => formatDate(p.value)
    // valueFormatter: d => d ? new Date(d.value).toLocaleDateString('pt-BR') : ''
  }

  const fieldTime = {
    valueFormatter: p => formatTime(p.value)
    // valueFormatter: d => d ? new Date(d.value).toLocaleDateString('pt-BR') : ''
  }
  const fieldCnpj = {
    valueFormatter: p => formatCNPJ(p.value)
  }
  const fieldFone = {
    valueFormatter: p => formatFone(p.value)
  }
  const fieldCel = {
    valueFormatter: p => formatCel(p.value)
  }
  const fieldCpf = {
    valueFormatter: p => formatCpf(p.value)
  }
  // const columnTypes = {
  //   currency: {
  //     width: 150,
  //     valueFormatter: currencyFormatter
  //   },
  //   ftFloat: fieldCurrency,
  //   ftInteger: fieldNumber,
  //   ftDate: fieldDate,
  //   ftCnpj: fieldCnpj,
  //   shaded: {
  //     cellClass: 'shaded-class'
  //   }
  // }
  const dataTypeWidthMapping: Record<ItemDataType, number> = {
    ftBoolean: 8 * gridFontSize,
    ftDate: 8 * gridFontSize,
    ftTime: 6 * gridFontSize,
    ftDateTime: 14 * gridFontSize,
    ftInteger: 10 * gridFontSize,
    ftString: 0,
    ftWideString: 0,
    ftFloat: 10 * gridFontSize,
    ftCurrency: 10 * gridFontSize
  }
  const cellDataTypeMapping: Record<ItemDataType, string> = {
    ftString: 'text',
    ftWideString: 'text',
    ftInteger: 'number',
    ftDate: 'date',
    ftTime: 'text',
    // ftDateTime: 'dateString',
    ftDateTime: 'text',
    ftBoolean: 'boolean',
    ftFloat: 'number',
    ftCurrency: 'number'
  }
  const alignmentMapping: Record<ItemAlignment, string> = {
    taLeftJustify: 'ag-left-aligned-cell',
    taRightJustify: 'ag-right-aligned-cell',
    taCenter: 'ag-center-aligned-cell'
  }
  const dataTypeWidth = (dataType: string, size?: number): number => {
    return dataTypeWidthMapping[dataType] || size * 6
  }
  const formatWidth = (item: DbGridColumn): number => {
    const width = dataTypeWidth(item.dataType, item.size)
    return width < dataTypeWidthMapping['ftBoolean'] ? dataTypeWidthMapping['ftBoolean'] : width
  }
  const formatCellClass = (item: DbGridColumn): string => {
    return alignmentMapping[item.alignment] || 'ag-default-cell'
  }
  const formatCellDataType = (item: DbGridColumn): string => {
    return cellDataTypeMapping[item.dataType] || 'text'
  }
  const formatDisplayLabel = (item: DbGridColumn): string => {
    return item.displayLabel
  }
  const formatField = (item: DbGridColumn): string => {
    return item.fieldName
  }
  const formatFilter = (item: DbGridColumn): string => {
    switch (item.dataType) {
      case 'ftString':
        return 'agTextColumnFilter'
      case 'ftInteger':
      case 'ftFloat':
        return 'agNumberColumnFilter'
      case 'ftDate':
        return 'agDateColumnFilter'
      case 'ftDateTime':
        return 'agDateColumnFilter'
      case 'ftBoolean':
        return 'agSetColumnFilter'
      default:
        return 'agTextColumnFilter'
    }
  }
  // Mapeia o tipo de dado para as configurações especiais de campo
  const getFieldTypeConfig = (dataType: ItemDataType) => {
    const typeConfigMap = {
      ftFloat: fieldReal,
      ftInteger: fieldNumber,
      ftDateTime: fieldDateTime,
      ftDate: fieldDate,
      ftTime: fieldTime
    }
    return typeConfigMap[dataType] || {}
  }

  // Mapeia o nome do campo para formatações especiais
  const getFieldNameConfig = (fieldName: string) => {
    if (fieldName.includes('ds_cnpj')) return fieldCnpj
    if (fieldName.includes('ds_cpf')) return fieldCpf
    if (fieldName.includes('ds_celular')) return fieldCel
    if (fieldName.includes('ds_telefone')) return fieldFone
    return {}
  }

  // Verifica se o tipo de dado permite agrupamento
  const isGroupableType = (dataType: ItemDataType): boolean => {
    return dataType === 'ftWideString' || dataType === 'ftString'
  }

  // Verifica se o tipo de dado permite agregação
  const isAggregableType = (dataType: ItemDataType): boolean => {
    return dataType === 'ftFloat' || dataType === 'ftInteger'
  }

  const getCols = (DbGridColumns: DbGridColumn[]) => {
    const visibleColumns = DbGridColumns?.filter(item => item.visible) || []

    return visibleColumns.map((item, index) => {
      // Configurações base da coluna
      const baseConfig = {
        field: formatField(item),
        headerName: formatDisplayLabel(item),
        cellDataType: formatCellDataType(item),
        width: formatWidth(item),
        filter: formatFilter(item),
        cellClass: formatCellClass(item),
        wrapText: false,
        autoHeight: false,
        enableRowGroup: isGroupableType(item.dataType),
        enablePivot: isGroupableType(item.dataType),
        enableValue: isAggregableType(item.dataType)
      }

      // Configurações especiais baseadas no tipo de dado
      const typeConfig = getFieldTypeConfig(item.dataType)

      // Configurações especiais baseadas no nome do campo
      const nameConfig = getFieldNameConfig(item.fieldName)

      // Ordena a primeira coluna por padrão
      const sortConfig = index === 0 ? { sort: 'asc' as const } : {}

      return {
        ...baseConfig,
        ...typeConfig,
        ...nameConfig,
        ...sortConfig
      }
    })
  }

  return {
    dataTypeWidth,
    getCols
  }
}
